/**
 * Room templates: the layouts a player picks from when building a room.
 *
 * Starter layouts are free. Bigger ones are sold for Gold (see premium.ts), so
 * the tier decides both the price and how many visitors a room may hold.
 * Extra room slots, for owning more than the free number of rooms, are sold
 * for Gold too.
 *
 * Big layouts are generated from a tile function rather than typed out, so the
 * shape of a 30 x 25 estate stays readable. A test parses every template and
 * checks that the door is on the floor, that every tile can be walked to from
 * the door within the pathfinding limit, and that the floor texture stays
 * under the size graphics cards guarantee.
 */

export const ROOM_TIERS = ['starter', 'medium', 'large', 'grand'] as const;
export type RoomTier = (typeof ROOM_TIERS)[number];

export const ROOM_TIER_INFO: Readonly<Record<RoomTier, { label: string; goldPrice: number }>> =
  Object.freeze({
    starter: { label: 'Starter', goldPrice: 0 },
    medium: { label: 'Medium', goldPrice: 150 },
    large: { label: 'Large', goldPrice: 400 },
    grand: { label: 'Grand', goldPrice: 900 },
  });

export interface RoomTemplate {
  id: string;
  label: string;
  tier: RoomTier;
  /** One line for the picker. */
  blurb: string;
  /** Heightmap rows; see heightmap.ts for the alphabet. */
  heightmap: readonly string[];
  door: { x: number; y: number };
  doorDirection: number;
  /** Most visitors at once (the server also applies its own global ceiling). */
  maxOccupants: number;
  /** Gold charged once, when the room is built. Zero for starter layouts. */
  goldPrice: number;
}

/** Widest or deepest a template may be. Keeps the baked floor texture under 4096 px. */
export const MAX_TEMPLATE_SIDE = 40;

export const ROOM_TEMPLATE_IDS = [
  'cosy',
  'square',
  'corner',
  'terrace',
  'hall',
  'loft',
  'lshape',
  'gallery',
  'courtyard',
  'ushape',
  'amphitheatre',
  'plaza',
  'arena',
  'estate',
  'megahall',
] as const;
export type RoomTemplateId = (typeof ROOM_TEMPLATE_IDS)[number];

/**
 * Builds rows from a tile function (`x`, `y` from the back-left corner), then
 * adds the door row: a wall of void with a two-tile gap at `doorX`. The tile
 * function's last row must be floor at `doorX` and `doorX + 1`.
 */
function layout(
  width: number,
  bodyHeight: number,
  doorX: number,
  tile: (x: number, y: number) => string,
): { heightmap: string[]; door: { x: number; y: number } } {
  const rows: string[] = [];
  for (let y = 0; y < bodyHeight; y += 1) {
    let row = '';
    for (let x = 0; x < width; x += 1) row += tile(x, y);
    rows.push(row);
  }
  let doorRow = '';
  for (let x = 0; x < width; x += 1) doorRow += x === doorX || x === doorX + 1 ? '0' : 'x';
  rows.push(doorRow);
  return { heightmap: rows, door: { x: doorX, y: bodyHeight } };
}

const inBox = (x: number, y: number, x0: number, y0: number, x1: number, y1: number) =>
  x >= x0 && x < x1 && y >= y0 && y < y1;

type Draft = Omit<RoomTemplate, 'goldPrice' | 'doorDirection'>;

const define = (draft: Draft): RoomTemplate => ({
  ...draft,
  doorDirection: 0,
  goldPrice: ROOM_TIER_INFO[draft.tier].goldPrice,
});

export const ROOM_TEMPLATES: Readonly<Record<RoomTemplateId, RoomTemplate>> = Object.freeze({
  /* ---- Starter: free ---- */
  cosy: define({
    id: 'cosy',
    label: 'Cosy Nook',
    tier: 'starter',
    blurb: 'A snug 6 x 6 for you and a few friends.',
    heightmap: ['000000', '000000', '000000', '000000', '000000', 'xx00xx'],
    door: { x: 2, y: 5 },
    maxOccupants: 12,
  }),
  square: define({
    id: 'square',
    label: 'Shophouse',
    tier: 'starter',
    blurb: 'A tidy 8 x 8 square.',
    heightmap: [
      '00000000',
      '00000000',
      '00000000',
      '00000000',
      '00000000',
      '00000000',
      '00000000',
      'xxx00xxx',
    ],
    door: { x: 3, y: 7 },
    maxOccupants: 20,
  }),
  corner: define({
    id: 'corner',
    label: 'Corner Kopitiam',
    tier: 'starter',
    blurb: 'An L-shaped corner unit with a wide front.',
    heightmap: [
      '000000000',
      '000000000',
      '000000000',
      '000xxxxxx',
      '000xxxxxx',
      '000xxxxxx',
      'x0xxxxxxx',
    ],
    door: { x: 1, y: 6 },
    maxOccupants: 25,
  }),
  terrace: define({
    id: 'terrace',
    label: 'Split-level Terrace',
    tier: 'starter',
    blurb: 'A raised back terrace over a lower floor.',
    heightmap: [
      '1111111111',
      '1111111111',
      '1111111111',
      '0000000000',
      '0000000000',
      '0000000000',
      '0000000000',
      'xxxx00xxxx',
    ],
    door: { x: 4, y: 7 },
    maxOccupants: 30,
  }),
  hall: define({
    id: 'hall',
    label: 'Grand Hall',
    tier: 'starter',
    blurb: 'A roomy 12 x 9 hall for parties.',
    heightmap: [
      '000000000000',
      '000000000000',
      '000000000000',
      '000000000000',
      '000000000000',
      '000000000000',
      '000000000000',
      '000000000000',
      '000000000000',
      'xxxxx00xxxxx',
    ],
    door: { x: 5, y: 9 },
    maxOccupants: 40,
  }),

  /* ---- Medium ---- */
  loft: define({
    id: 'loft',
    label: 'Open Loft',
    tier: 'medium',
    blurb: '14 x 12 with a raised mezzanine along the back wall.',
    ...layout(14, 11, 6, (_x, y) => (y < 3 ? '1' : '0')),
    maxOccupants: 40,
  }),
  lshape: define({
    id: 'lshape',
    label: 'L-Shaped Shophouse',
    tier: 'medium',
    blurb: 'Two wings around a corner. Good for a shop and a lounge.',
    ...layout(14, 13, 3, (x, y) => (x >= 7 && y < 6 ? 'x' : '0')),
    maxOccupants: 45,
  }),
  gallery: define({
    id: 'gallery',
    label: 'Long Gallery',
    tier: 'medium',
    blurb: 'A 22 x 6 strip for exhibitions, catwalks and races.',
    ...layout(22, 5, 10, () => '0'),
    maxOccupants: 40,
  }),

  /* ---- Large ---- */
  courtyard: define({
    id: 'courtyard',
    label: 'Courtyard Villa',
    tier: 'large',
    blurb: '18 x 18 ring of rooms around an open courtyard.',
    ...layout(18, 17, 8, (x, y) => (inBox(x, y, 6, 5, 12, 11) ? 'x' : '0')),
    maxOccupants: 60,
  }),
  ushape: define({
    id: 'ushape',
    label: 'U-Shaped Compound',
    tier: 'large',
    blurb: 'Two wings joined by a long front hall.',
    ...layout(18, 15, 8, (x, y) => (inBox(x, y, 5, 0, 13, 9) ? 'x' : '0')),
    maxOccupants: 60,
  }),
  amphitheatre: define({
    id: 'amphitheatre',
    label: 'Amphitheatre',
    tier: 'large',
    blurb: 'Stepped seating rising to the back, stage at the front.',
    ...layout(20, 15, 9, (_x, y) => (y < 3 ? '3' : y < 6 ? '2' : y < 9 ? '1' : '0')),
    maxOccupants: 80,
  }),
  plaza: define({
    id: 'plaza',
    label: 'Open Plaza',
    tier: 'large',
    blurb: 'A wide open 20 x 20 floor. Build anything.',
    ...layout(20, 19, 9, () => '0'),
    maxOccupants: 80,
  }),

  /* ---- Grand ---- */
  arena: define({
    id: 'arena',
    label: 'Arena',
    tier: 'grand',
    blurb: 'A rounded 26 x 26 floor with a raised centre stage.',
    ...layout(26, 25, 12, (x, y) => {
      // Cut each corner diagonally; the middle of the floor is a raised stage.
      const cut = 7;
      const right = 25 - x;
      const front = 24 - y;
      if (x + y < cut || right + y < cut || x + front < cut || right + front < cut) return 'x';
      return inBox(x, y, 10, 9, 16, 16) ? '1' : '0';
    }),
    maxOccupants: 100,
  }),
  estate: define({
    id: 'estate',
    label: 'Grand Estate',
    tier: 'grand',
    blurb: '30 x 26 with a raised terrace and two open courtyards.',
    ...layout(30, 25, 14, (x, y) => {
      if (inBox(x, y, 6, 10, 11, 18) || inBox(x, y, 19, 10, 24, 18)) return 'x';
      return y < 6 ? '1' : '0';
    }),
    maxOccupants: 100,
  }),
  megahall: define({
    id: 'megahall',
    label: 'Mega Hall',
    tier: 'grand',
    blurb: 'The biggest floor there is: 32 x 32 with a back stage.',
    ...layout(32, 31, 15, (x, y) => (inBox(x, y, 9, 0, 23, 4) ? '1' : '0')),
    maxOccupants: 100,
  }),
});

/** Templates of one tier, in the order players see them. */
export function templatesInTier(tier: RoomTier): RoomTemplate[] {
  return ROOM_TEMPLATE_IDS.map((id) => ROOM_TEMPLATES[id]).filter(
    (template) => template.tier === tier,
  );
}

/* -------------------------------------------------------------------------- */
/* Room limit                                                                 */
/* -------------------------------------------------------------------------- */

/** Rooms every player may own without paying. */
export const MAX_ROOMS_PER_USER = 5;

/**
 * Gold for each extra room slot, in the order they are bought. The list length
 * is the most slots anyone can add (so at most 15 rooms in all).
 */
export const ROOM_SLOT_PRICES: readonly number[] = Object.freeze([
  50, 50, 100, 100, 150, 150, 250, 250, 400, 400,
]);

export const MAX_EXTRA_ROOM_SLOTS = ROOM_SLOT_PRICES.length;

/** How many rooms a player with `extraSlots` bought slots may own. */
export function roomLimit(extraSlots: number): number {
  return MAX_ROOMS_PER_USER + Math.min(Math.max(Math.trunc(extraSlots), 0), MAX_EXTRA_ROOM_SLOTS);
}

/** Gold the next slot costs, or null when every slot is already bought. */
export function nextRoomSlotPrice(extraSlots: number): number | null {
  return ROOM_SLOT_PRICES[Math.max(Math.trunc(extraSlots), 0)] ?? null;
}

/** What the client needs to show the room limit and the price of more. */
export interface RoomQuota {
  owned: number;
  limit: number;
  extraSlots: number;
  /** Gold for the next slot; null when none are left to buy. */
  nextSlotPrice: number | null;
  gold: number;
}
